/**
 * Application root: providers, error boundary, router.
 *
 * The landing page sits outside the providers deliberately. `DataProvider`
 * prefetches categories, doctors and packages for every visitor — that is right
 * for the app, where those are needed within a click or two, but the landing
 * page is a public marketing page that uses none of them. Mounting it inside
 * would cost three API round-trips on the highest-traffic page in the product,
 * and log console errors there whenever the API is having a bad day.
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { DataProvider } from './context/DataContext';
import { NotificationProvider } from './context/NotificationContext';
import { PermissionProvider } from './context/PermissionContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { RouteFallback } from './app/guards';
import { AppRoutes } from './app/router';

/**
 * The two provider-free pages are split out of the main bundle.
 *
 * The patient's visit page is the reason. It is opened from a WhatsApp message,
 * on a phone, on whatever signal the person has outside a clinic, by somebody
 * who will read it once — and without this it would arrive bundled with the
 * entire signed-in application, none of which it uses. The landing page is
 * split for the same reason and has the highest traffic in the product.
 *
 * `RouteFallback` was written in guards.jsx for suspended route chunks and had
 * no caller until now.
 */
const LandingPage = lazy(() =>
  import('./features/landing/LandingPage').then((m) => ({
    default: m.LandingPage,
  })),
);
const PublicVisitPage = lazy(() =>
  import('./features/public/PublicVisitPage').then((m) => ({
    default: m.PublicVisitPage,
  })),
);

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Public, provider-free */}
            <Route path="/landingpage" element={<LandingPage />} />

            {/*
            The patient's visit page, opened from a WhatsApp link with no
            account and no session. Outside the providers for the same reason
            the landing page is, and one more: AuthProvider would read a stale
            `capsule_session` from a shared clinic tablet and render the shell
            around somebody else's appointment. This page must know nothing
            about who is signed in.
          */}
            <Route path="/v/:token" element={<PublicVisitPage />} />

            {/* Everything else runs inside the app's data and auth layers */}
            <Route
              path="*"
              element={
                <AuthProvider>
                  <PermissionProvider>
                    <NotificationProvider>
                      <DataProvider>
                        <AppRoutes />
                      </DataProvider>
                    </NotificationProvider>
                  </PermissionProvider>
                </AuthProvider>
              }
            />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
